// src/store/six-detail/sixDetailSlice.ts
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { SixDetailState } from "@/types/six/SixState";
import type { SixFile } from "@/types/six/Six";
import { initSixDetail } from "@/store/thunks/sixDetail/getInitialDetailThunk";
import { uploadFile } from "@/store/thunks/sixDetail/uploadFileThunk";
import { createFolder } from "@/store/thunks/sixDetail/createFolderThunk";
import { deleteFolder } from "@/store/thunks/sixDetail/deleteFolderThunk";
import { fetchActivity } from "@/store/thunks/sixDetail/fetchActivityThunk";
import { fetchParticipants } from "@/store/thunks/sixDetail/fetchParticipantsThunk";
import { refreshFilesFromApi } from "@/store/thunks/sixDetail/refreshFilesThunk";
import { deleteFileThunk } from "@/store/thunks/sixDetail/deleteFileThunk";

const initialState: SixDetailState = {
  sixId: undefined,
  files: [],
  folders: [],
  currentPath: '/',
  activity: [],
  participants: undefined,
  loadingFiles: false,
  loadingActivity: false,
  uploadProgress: undefined,
  error: undefined,
  meta: undefined
};

const UPLOAD_PROGRESS = 0; // Placeholder for upload progress, can be updated as needed

export const sixDetailSlice = createSlice({
  name: "sixDetail",
  initialState,
  reducers: {
    setUploadProgress(state, action: { payload: number }) {
      const { payload } = action;
      state.uploadProgress = payload;
    },
    resetSixDetailError(state) {
      state.error = undefined;
    },
    resetSixDetailState() {
      return initialState;
    },
    /**
     * Add a file to the files array (for real-time updates from P2P).
     * Prevents duplicates by checking CID.
     */
    addApiFile(state, action: PayloadAction<SixFile>) {
      const exists = state.files.some(f => f.cid === action.payload.cid);
      if (!exists) {
        state.files.push(action.payload);
      }
    },
    /**
     * Set the current folder path for navigation
     */
    setCurrentPath(state, action: PayloadAction<string>) {
      state.currentPath = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // INIT
      .addCase(initSixDetail.pending, (state) => {
        state.loadingFiles = true;
        state.loadingActivity = true;
        state.error = undefined;
      })
      .addCase(initSixDetail.fulfilled, (state, action) => {
        const { payload : { sixId, meta, files, folders, activity, participants} }= action;
        state.sixId = sixId;
        state.files = files;
        state.folders = folders;
        // Path is managed by the hook via URL - don't reset here
        state.activity = activity;
        state.participants = participants;
        state.meta = meta;
        state.loadingFiles = false;
        state.loadingActivity = false;
      })
      .addCase(initSixDetail.rejected, (state, action) => {
        state.loadingFiles = false;
        state.loadingActivity = false;
        const { error: {message} } = action;
        state.error = message;
      })
      // UPLOAD FILE
      .addCase(uploadFile.pending, (state) => {
        state.uploadProgress = UPLOAD_PROGRESS;
        state.error = undefined;
      })
      .addCase(uploadFile.fulfilled, (state, action) => {
        state.files.push(action.payload);
        state.uploadProgress = undefined;
      })
      .addCase(uploadFile.rejected, (state, action) => {
        state.uploadProgress = undefined;
        const { error: {message} } = action;
        state.error = message;
      })
      // CREATE FOLDER - uses fresh data from API after CQRS polling completes
      .addCase(createFolder.fulfilled, (state, action) => {
        const { files, folders } = action.payload;
        state.files = files;
        state.folders = folders;
      })
      .addCase(createFolder.rejected, (state, action) => {
        const { error: {message} } = action;
        state.error = message;
      })
      // DELETE FOLDER - uses fresh data from API after CQRS polling completes
      .addCase(deleteFolder.fulfilled, (state, action) => {
        const { files, folders } = action.payload;
        state.files = files;
        state.folders = folders;
      })
      .addCase(deleteFolder.rejected, (state, action) => {
        const { error: { message } } = action;
        state.error = message;
      })
      // FETCH ACTIVITY
      .addCase(fetchActivity.pending, (state) => {
        state.loadingActivity = true;
        state.error = undefined;
      })
      .addCase(fetchActivity.fulfilled, (state, action) => {
        const { payload } = action;
        state.activity = payload;
        state.loadingActivity = false;
      })
      .addCase(fetchActivity.rejected, (state, action) => {
        state.loadingActivity = false;
        const { error: {message} } = action;
        state.error = message;
      })
      // Participants
       .addCase(fetchParticipants.pending, (state) => {
        state.loadingActivity = true;
        state.error = undefined;
      })
      .addCase(fetchParticipants.fulfilled, (state, action) => {
        const { payload } = action;
        state.participants = payload;
        state.loadingActivity = false;
      })
      .addCase(fetchParticipants.rejected, (state, action) => {
        state.loadingActivity = false;
        const { error: {message} } = action;
        state.error = message;
      })
      // REFRESH FILES (from file-list P2P message)
      .addCase(refreshFilesFromApi.fulfilled, (state, action) => {
        const { payload: { files, folders, skipped } } = action;
        if (!skipped) {
          state.files = files;
          state.folders = folders;
        }
      })
      // DELETE FILE
      .addCase(deleteFileThunk.fulfilled, (state, action) => {
        const deletedFileId = action.payload.fileId;
        state.files = state.files.filter((file) => file.id !== deletedFileId);
      })
      .addCase(deleteFileThunk.rejected, (state, action) => {
        const { error: { message } } = action;
        state.error = message;
      });
  },
});
const { actions } = sixDetailSlice;
export const { setUploadProgress, resetSixDetailError, resetSixDetailState, addApiFile, setCurrentPath } = actions;
export default sixDetailSlice.reducer;
